import { getUser, getUserZendeskToken } from '../commands/userCommands';
import { auth } from '../data-types/initialisation/initialisationConstants';
import { SIGNUP_CHECKPOINT } from '../data-types/signup/signupEnums';
import { DEFAULT_RETRY_INTERVAL, TIMEOUT_MS } from '../data-types/timing/timingConstants';
import { UserStatus } from '../data-types/user/userEnums';
import {
  BETKWIFF_ZENDESK,
  BETKWIFF_ZENDESK_FAQ_URL,
  initialClassicWebWidgetSettings,
  KWIFF_ZENDESK,
  KWIFF_ZENDESK_FAQ_URL,
  TEST_SANDBOX_ZENDESK,
} from '../data-types/zendesk/zendeskConstants';
import { WIDGET, WIDGET_TYPE } from '../data-types/zendesk/zendeskEnums';
import { isBetkwiffDomain, isProjectPhoenix } from '../international/internationalUtils';
import { DEFAULT_REDIRECTS, PATHS } from '../routing-system/appPaths';
import browserHistory from '../routing-system/browserHistory';
import { setChatCookieDialog } from '../state/actions/dialogActions';
import { setIsZendeskOpen } from '../state/actions/iframeActions';
import { setPreventRedirectOnError } from '../state/actions/navigationActions';
import { store } from '../state/store';
import { alreadyAllowsCookies } from '../utils/cookies';
import { isProductionEnv } from '../utils/general';
import { handleSignupDynamicEndpointRequest } from '../utils/signup';

const getProductionZendeskSource = () => {
  if (isProjectPhoenix()) {
    return '';
  }

  return isBetkwiffDomain() ? BETKWIFF_ZENDESK : KWIFF_ZENDESK;
};

const getDevelopmentZendeskSource = () => {
  if (isProjectPhoenix()) {
    return '';
  }

  return isBetkwiffDomain() ? BETKWIFF_ZENDESK : TEST_SANDBOX_ZENDESK;
};

const getZendeskSource = () => (isProductionEnv() ? getProductionZendeskSource() : getDevelopmentZendeskSource());

const waitUntilZendeskIsDefined = (callback: () => void) => {
  const interval = setInterval(() => {
    if (window.zE) {
      callback();
      clearInterval(interval);
    }
  }, DEFAULT_RETRY_INTERVAL);
};

const hideWidget = (widget: WIDGET) => {
  try {
    window.zE(widget, 'hide');
  } catch {
    // ignore
  }
};

const hide = () => {
  switch (window.zE && window.zE.widget) {
    case WIDGET_TYPE.MESSENGER: {
      hideWidget(WIDGET.MESSENGER);
      return;
    }
    case WIDGET_TYPE.CLASSIC: {
      hideWidget(WIDGET.WEB_WIDGET);
      return;
    }
    default:
      return;
  }
};

const setOnCloseWidget = (widget: WIDGET) => {
  try {
    window.zE(`${widget}:on`, 'close', onCloseCallback);
  } catch {
    // ignore
  }
};

const setOnClose = () => {
  switch (window.zE && window.zE.widget) {
    case WIDGET_TYPE.MESSENGER: {
      setOnCloseWidget(WIDGET.MESSENGER);
      return;
    }
    case WIDGET_TYPE.CLASSIC: {
      setOnCloseWidget(WIDGET.WEB_WIDGET);
      return;
    }
    default:
      return;
  }
};

const isUserInAdvancedStatus = () => {
  const { user, authentication } = store.getState().userData;
  const isAuthenticated = authentication === auth.AUTHENTICATED;
  const hasUnverifiedStatus = user && user.status === UserStatus.UNVERIFIED;
  const hasVerifiedStatus = user && user.status === UserStatus.ACTIVE;

  return isAuthenticated || hasUnverifiedStatus || hasVerifiedStatus;
};

const onCloseCallback = async () => {
  let user;
  const path = browserHistory.location.pathname;
  setIsZendeskOpen(false);
  logoutZendeskUser();
  setPreventRedirectOnError(true);

  try {
    const {
      signup: { signupV3 },
    } = store.getState();
    const { useSignupV3, checkpoint } = signupV3;

    if (useSignupV3 && checkpoint && checkpoint.checkpoint === SIGNUP_CHECKPOINT.UK_ACCOUNT_VERIFICATION) {
      await getUser();
      user = store.getState().userData.user;
      if (user && user.status === UserStatus.ACTIVE) {
        await handleSignupDynamicEndpointRequest(checkpoint);
      }
    }

    if (path === PATHS.SIGNUP.STEPS.UNVERIFIED) {
      await getUser();
      user = store.getState().userData.user;
      if (user && user.status === UserStatus.ACTIVE) {
        browserHistory.push(PATHS.SIGNUP.STEPS.VERIFIED);
      }
    }

    if (isUserInAdvancedStatus() && [DEFAULT_REDIRECTS.SPORTS, DEFAULT_REDIRECTS.CASINO, PATHS.APP.PROFILE.WITHDRAWAL].includes(path)) {
      await getUser();
    }
  } catch {
    // Do nothing. This error means that an unauthenticated user opened the live chat
  } finally {
    setPreventRedirectOnError(false);
  }

  hide();

  setChatAsDefault();
};

const identifyClassicWebWidgetUser = () => {
  const user = store.getState().userData.user;
  const firstName = user && user.name && user.name.firstName;
  const lastName = user && user.name && user.name.lastName;
  const areFirstNameAndSurnamePresent = firstName && lastName;

  if (areFirstNameAndSurnamePresent) {
    try {
      window.zE(WIDGET.WEB_WIDGET, 'identify', {
        name: `${firstName} ${lastName}`,
        email: user.email,
        organization: 'kwiff',
      });
    } catch {
      // ignore
    }
  }
};

const identifyMessengerUser = async () => {
  if (isUserInAdvancedStatus()) {
    try {
      setPreventRedirectOnError(true);

      const token = await getUserZendeskToken();

      if (token) {
        window.zE(WIDGET.MESSENGER, 'loginUser', (callback: (_: string) => void) => {
          callback(token);
        });
      }
    } catch {
      // ignore
    } finally {
      setPreventRedirectOnError(false);
    }
  }
};

const identifyUser = async () => {
  switch (window.zE && window.zE.widget) {
    case WIDGET_TYPE.MESSENGER: {
      await identifyMessengerUser();
      return;
    }
    case WIDGET_TYPE.CLASSIC: {
      identifyClassicWebWidgetUser();
      return;
    }
    default:
      return;
  }
};

const initialize = async () => {
  const zendeskScriptSource = getZendeskSource();

  if (zendeskScriptSource) {
    const script = document.createElement('script');
    script.src = zendeskScriptSource;
    script.id = 'ze-snippet';
    await document.body.appendChild(script);
    script.onload = async () => {
      window.zESettings = initialClassicWebWidgetSettings;

      hide();
      waitUntilZendeskIsDefined(() => setOnClose());
    };
  }
};

const setChatAsDefault = () => {
  if (window.zE && window.zE.widget === WIDGET_TYPE.CLASSIC) {
    try {
      window.zE(WIDGET.WEB_WIDGET, 'updateSettings', {
        webWidget: {
          helpCenter: {
            suppress: true,
          },
          chat: {
            suppress: false,
          },
        },
      });
    } catch {
      // ignore
    }
  }
};

const setFAQsAsDefault = () => {
  if (window.zE && window.zE.widget === WIDGET_TYPE.CLASSIC) {
    try {
      window.zE(WIDGET.WEB_WIDGET, 'updateSettings', {
        webWidget: {
          helpCenter: {
            suppress: false,
          },
          chat: {
            suppress: false,
          },
        },
      });
    } catch {
      // ignore
    }
  }
};

const openWidget = (widget: WIDGET) => {
  try {
    window.zE(widget, 'show');
    window.zE(widget, 'open');
    window.zE(`${widget}:on`, 'close', () => {
      window.zE(widget, 'hide');
    });
  } catch {
    // ignore
  }
};

const open = () => {
  switch (window.zE && window.zE.widget) {
    case WIDGET_TYPE.MESSENGER: {
      openWidget(WIDGET.MESSENGER);
      return;
    }
    case WIDGET_TYPE.CLASSIC: {
      openWidget(WIDGET.WEB_WIDGET);
      return;
    }
    default:
      return;
  }
};

const openASAP = () => {
  const timeout = setTimeout(() => {
    open();
    setIsZendeskOpen(true);
    clearTimeout(timeout);
  }, TIMEOUT_MS.ASAP);
};

const widgetOpen = async (defaultOption: () => void, onOpen?: () => void) => {
  if (!alreadyAllowsCookies()) {
    setChatCookieDialog(true);
  } else {
    if (!window.zE) {
      await initialize();
    }

    waitUntilZendeskIsDefined(async () => {
      await identifyUser();
      defaultOption();
      openASAP();
    });

    if (onOpen) {
      onOpen();
    }
  }
};

export const startZendeskChat = (callback?: () => void) => widgetOpen(setChatAsDefault, callback);

export const startFAQs = () => {
  if (window.zE && window.zE.widget === WIDGET_TYPE.CLASSIC) {
    widgetOpen(setFAQsAsDefault);
    return;
  }

  const isBetkwiff = isBetkwiffDomain();
  const faqUrl = isBetkwiff ? BETKWIFF_ZENDESK_FAQ_URL : KWIFF_ZENDESK_FAQ_URL;

  window.open(faqUrl, '_blank');
};

const logoutMessengerUser = () => {
  try {
    window.zE(WIDGET.MESSENGER, 'logoutUser');
  } catch {
    // ignore
  }
};

export const logoutZendeskUser = () => {
  switch (window.zE && window.zE.widget) {
    case WIDGET_TYPE.MESSENGER: {
      logoutMessengerUser();
      return;
    }
    case WIDGET_TYPE.CLASSIC:
      // classic widget API does not have logout functionality
      return;
    default:
      return;
  }
};
